/* Header payload loader (2026-09-11).
   The mega-menu panels, mobile drawer panels and country list used to ship as
   ~106 KB of inert raw text inside every document. They now arrive on first
   intent from sections/header-payloads.liquid via the Section Rendering API and
   are appended to the same mount the controllers already read from
   ([data-ss-deferred-header-payloads]), so hydration is unchanged.

   Repeat views in the same tab reuse the HTML from sessionStorage, keyed by the
   mount's data-ss-payload-key (theme id + locale + country + a Liquid version).
   A missing or unreadable store falls back to fetch, same as the first view.

   A cached copy is replayed only while it is younger than CACHE_TTL. The
   payload carries prices and the quick-add size chips, and the key changes only
   with theme, locale, country or ss_hp_ver, so without an expiry a size that
   sold out or a price that changed stayed wrong for the whole tab (and
   /cart/add.js accepts a sold-out variant with a 200, d31f3b98f). Past the TTL
   the view behaves exactly like a first view: it fetches (on idle or intent)
   instead of painting the old copy and swapping later, because a swap would
   replace panels the shopper may already be hovering or tapping. Entries from
   before the stamp existed have no timestamp and are treated as expired. If the
   refetch fails, the expired copy is used rather than no deep panels at all.
   Intent triggers are unchanged: pointer/focus/touch on the header nav,
   hamburger or locale trigger; otherwise real idle after `load` (no forced
   timeout) unless the visitor asked for data saving or is on a 2G-class link.

   The promise is exposed as window.SacrasoulHeaderPayloads.ready so a controller
   whose intent beat the fetch can wait and retry once; `ss:header-payloads`
   fires on the document when the nodes are in place. */
(function () {
  var mount = document.querySelector('[data-ss-deferred-header-payloads][data-ss-payload-src]');
  if (!mount || window.SacrasoulHeaderPayloads) return;
  var src = mount.getAttribute('data-ss-payload-src');
  var key = mount.getAttribute('data-ss-payload-key') || ('hp:' + src);
  var promise = null;
  var done = false;
  /* 10 minutes: long enough that a browsing burst (the case the cache exists
     for) still gets instant repeat views, short enough that a mid-session sell
     out or price change reaches the menu within a few page views. */
  var CACHE_TTL = 10 * 60 * 1000;

  function inject(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    /* Only the raw-text sources travel; anything else the API wrapper
       carries (the section div, a proxy-injected script) stays behind. */
    var sources = tpl.content.querySelectorAll('[data-ssm-panel-source], [data-ssd-panel-source], [data-region-source]');
    if (!sources.length) throw new Error('header payloads empty');
    for (var i = 0; i < sources.length; i++) mount.appendChild(sources[i]);
    done = true;
    document.dispatchEvent(new CustomEvent('ss:header-payloads'));
    return true;
  }

  /* Stored as "<ms timestamp>|<html>" under the same key. */
  function readCache() {
    var raw;
    try { raw = sessionStorage.getItem(key); } catch (e) { return null; }
    if (!raw) return null;
    var bar = raw.indexOf('|');
    var at = bar > 0 ? Number(raw.slice(0, bar)) : NaN;
    if (!(at > 0)) return null;
    var age = Date.now() - at;
    return { html: raw.slice(bar + 1), fresh: age >= 0 && age < CACHE_TTL };
  }

  function writeCache(html) {
    try { sessionStorage.setItem(key, Date.now() + '|' + html); } catch (e) {}
  }

  function fetchNetwork() {
    return fetch(src, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('header payloads ' + r.status); return r.text(); })
      .then(function (html) {
        writeCache(html);
        return inject(html);
      });
  }

  function load() {
    if (promise) return promise;
    var cached = readCache();
    if (cached && cached.fresh) {
      promise = Promise.resolve().then(function () {
        try {
          return inject(cached.html);
        } catch (err) {
          try { sessionStorage.removeItem(key); } catch (e) {}
          return fetchNetwork();
        }
      }).catch(function () {
        promise = null;
        return false;
      });
      return promise;
    }
    promise = fetchNetwork().catch(function () {
      /* An expired copy beats no deep panels when the network fails. */
      if (cached && !done) {
        try { return inject(cached.html); } catch (e) {}
      }
      /* Allow a retry on the next intent; a menu with no deep panels still
         navigates through its ordinary links. */
      promise = null;
      return false;
    });
    return promise;
  }

  window.SacrasoulHeaderPayloads = {
    load: load,
    get ready() { return promise; },
    get done() { return done; }
  };

  var intentTargets = ['.ssm-nav', '#mobileMenuToggle', '[data-locale-trigger]', '.hamburger-enhanced'];
  var opts = { passive: true };
  intentTargets.forEach(function (sel) {
    var el = document.querySelector(sel);
    if (!el) return;
    ['pointerover', 'pointerdown', 'focusin', 'touchstart'].forEach(function (ev) {
      el.addEventListener(ev, function () { load(); }, opts);
    });
  });

  function saveData() {
    var c = navigator.connection;
    return !!(c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '')));
  }
  function idle() {
    if (promise || saveData()) return;
    if (window.requestIdleCallback) requestIdleCallback(function () { load(); });
    else setTimeout(load, 4000);
  }
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
})();
